import React, { useState } from 'react';
import type { VideoProject } from './videoStudioTypes';
import { videoStudioApi } from './videoStudioApi';
import { VeoGenerationCard } from './VeoGenerationCard';
import { FlowExportCard } from './FlowExportCard';
import { VideoVersionItem } from './VideoVersionItem';

interface VideoVersionsPanelProps {
  project: VideoProject;
  onRefresh: () => void;
  onVersionSelected: (videoUrl: string) => void;
  onBeforeGenerate?: () => Promise<any>;
}

export const VideoVersionsPanel: React.FC<VideoVersionsPanelProps> = ({
  project,
  onRefresh,
  onVersionSelected,
  onBeforeGenerate,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleSelect = async (versionId: string, url: string) => {
    setIsSubmitting(true);
    setActionError(null);
    try {
      const res = await videoStudioApi.selectVideoVersion(
        project.campaign_id,
        project.creative_id,
        versionId
      );
      if (res.ok) {
        onVersionSelected(url);
        onRefresh();
      }
    } catch (err: any) {
      setActionError(err.message || 'Erro ao selecionar versão');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVeoSuccess = (videoUrl: string) => {
    onVersionSelected(videoUrl);
    onRefresh();
  };

  const handleFlowImported = (videoUrl: string, makeActive: boolean) => {
    if (makeActive) {
      onVersionSelected(videoUrl);
    }
    onRefresh();
  };

  return (
    <div>
      <div style={{ marginBottom: '1.25rem' }}>
        <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Versões de Vídeo Renderizadas</h4>
        <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.825rem', color: 'var(--text-secondary, #64748b)' }}>
          Renderize automaticamente via Google Cloud com Veo 3.1 e Veo Fast, ou use o Google Flow como alternativa manual.
        </p>
      </div>

      {/* Card de Geração Automática Google Cloud Veo 3.1 & Veo Fast */}
      <VeoGenerationCard
        campaignId={project.campaign_id}
        creativeId={project.creative_id}
        targetDurationSeconds={
          (project.scenes || []).reduce((acc, s) => acc + (Number(s.duration_seconds) || 0), 0)
          || project.target_duration_seconds
          || 15
        }
        sceneCount={(project.scenes || []).length}
        onBeforeGenerate={onBeforeGenerate}
        onSuccess={handleVeoSuccess}
        onError={setActionError}
      />

      {actionError && (
        <div style={{ padding: '0.75rem', borderRadius: '0.5rem', background: '#fee2e2', color: '#991b1b', marginBottom: '1rem', fontSize: '0.85rem' }}>
          {actionError}
        </div>
      )}

      {/* Lista de Versões Existentes */}
      <div style={{ marginBottom: '1.5rem' }}>
        {project.versions?.length === 0 ? (
          <div className="vstudio-card" style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ margin: 0, color: 'var(--text-secondary, #64748b)', fontSize: '0.875rem' }}>
              Nenhuma versão de vídeo foi vinculada a este criativo ainda. Gere acima via Veo 3.1 ou importe pelo Flow.
            </p>
          </div>
        ) : (
          project.versions?.map((version, idx) => {
            const isSelected = Boolean(version.id === project.selected_version_id || version.is_selected);
            return (
              <VideoVersionItem
                key={version.id}
                version={version}
                index={project.versions.length - idx}
                isSelected={isSelected}
                isSubmitting={isSubmitting}
                onSelect={handleSelect}
              />
            );
          })
        )}
      </div>

      {/* Alternativa Manual: Google Flow & Importação Direta */}
      <FlowExportCard
        campaignId={project.campaign_id}
        creativeId={project.creative_id}
        onVersionImported={handleFlowImported}
        onError={setActionError}
      />
    </div>
  );
};
